import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import MultilineTextBoxViewModel from '@/ViewModels/Controls/MultilineTextBoxViewModel';
import MultilineTextBoxPopUpLinkType from '@/Components/Links/MultilineTextBoxPopUpLink/MultilineTextBoxPopUpLink';
import Utilities from '@/Utility/Utilities';

export default class MultilineTextBox extends Control {
    private MultilineTextBoxPopUpLink = ComponentManager.getControl<typeof MultilineTextBoxPopUpLinkType>('MultilineTextBoxPopUpLink');

    private jQueryControl: JQuery;
    private elementType: string;
    private inputFieldOrTextarea: JQuery;
    private link: MultilineTextBoxPopUpLinkType;
    
    private readonly verticalScrollableClassName = 'vertical-scrollable';
    private readonly horizontalScrollableClassName = 'horizontal-scrollable';
    private readonly hasMultipleLinksClassName = 'has-multiple-links';
    private readonly isInGridClassName = 'is-in-grid';

    public constructor(private viewModel: MultilineTextBoxViewModel, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    /**
     * @override 
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.elementType = '';

        if (this.viewModel.isMultiline()) {
            this.jQueryControl = this.element || $('<div><textarea></textarea></div>');
            this.elementType = 'textarea';
        }
        else {
            this.jQueryControl = this.element || $('<div><input type="text"></div>');
            this.elementType = 'input';
        }

        this.inputFieldOrTextarea = this.jQueryControl.find(this.elementType);
        if (this.inputFieldOrTextarea.length == 0) {
            this.jQueryControl.append(`<${this.elementType}>`);
            this.inputFieldOrTextarea = this.jQueryControl.find(this.elementType);
        }

        this.applyBindings(this.inputFieldOrTextarea, {
            focus: this.context.isFocusable,

            tooltip: this.context.tooltip
        });

        if (this.viewModel.enablePopUp()) {

            const linkVm = this.viewModel.createMultilineTextBoxPopUpLinkViewModel();
            this.link = new this.MultilineTextBoxPopUpLink(linkVm);
            this.link.getTuple().control.appendTo(this.jQueryControl);
            this.repositionLink();
        } 

        this.widget(null);

        return this.jQueryControl;
    }

    protected onWidgetCreatedCallback(widget: null): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'multilinetextbox',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            height: this.viewModel.height,

            visible: this.viewModel.isVisible,

            invalid: this.viewModel.invalidationText,

            styles: this.definePureComputed({
                read: () => {
                    const styles = [];

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    const states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled() === true) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    if (this.viewModel.isMultiline() === true) {
                        states.push('multiline');
                    } else {
                        states.push('singleline');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.maxLength >= 0) {
            this.inputFieldOrTextarea.attr('maxlength', this.viewModel.maxLength);
        }

        this.applyBindings(this.inputFieldOrTextarea, {
            classes: ['k-textbox'],

            value: this.viewModel.value,

            readonly: this.defineComputed(() => {
                const value = this.viewModel.isEnabled();
                return !value;
            }),

            locator: this.viewModel.locator,

            attr: {
                lang: this.viewModel.language
            }
        });

        if (this.viewModel.onChange != null) {
            this.defineSubscription(this.viewModel.value, (value) => {
                if(this.viewModel.enablePopUp())
                    this.repositionLink();
                
                this.viewModel.onChange(value);
            });
        }

        if (this.viewModel.onKeyDown != null) {
            this.addEventHandler('keydown', this.jQueryControl, (event: JQuery.KeyDownEvent) => {
                this.viewModel.onKeyDown(event.keyCode);
            });
        }

        this.addEventHandler('keyup', this.jQueryControl, (event: JQuery.KeyUpEvent) => {
            if (! (event.key === 'Enter' && (event.shiftKey || event.ctrlKey))) {
                event.stopPropagation();
            }

            if (this.viewModel.isEnabled())
                this.viewModel.value($(event.target).val() as string);
            
            if (this.viewModel.onKeyUp != null) {
                this.viewModel.onKeyUp(event.keyCode);
            }
        }, this.elementType);
        
        this.applyBindings(this.jQueryControl, {
            hotkey: {
                shortcut: ['backspace', 'space'],
                handler: (e: ExtendedKeyboardEvent) => { e.stopPropagation(); return true; },
                isGlobal: false
            }
        });
    }

    public addLabelReference(label: JQuery): JQuery {
        const id = this.identifier + '-' + this.elementType;
        this.inputFieldOrTextarea.attr('id', id);
        label.attr('for', id);
        return label;
    }

    public repositionLink(): void {
        this.repositionLinkInternal();
    }
    
    private repositionLinkInternal(retriesLeft = 10): void {
        if (this.isDisposed || this.link == null)
            return;

        const $el = this.inputFieldOrTextarea;
        if ($el.length == 0)
            return;
        
        // Element ist zwar vorhanden, hat aber noch keine Scroll-Height (ist also nicht im DOM gerendert)
        if ($el.get(0).scrollHeight === 0) {
            if (retriesLeft <= 0)
                return;
            
            // Nach kurzer Zeit neu probieren
            window.setTimeout(() => {
                this.repositionLinkInternal(--retriesLeft);
            }, 250);
            return;
        }

        const hasMultipleLinks = this.jQueryControl.children('.c-link').length >= 2;
        if (hasMultipleLinks)
            this.jQueryControl.addClass(this.hasMultipleLinksClassName)
        else
            this.jQueryControl.removeClass(this.hasMultipleLinksClassName)

        const el = $el.get(0);
        
        const isInCFGrid = $el.parents('.cf-grid-row').length > 0;
        if (isInCFGrid) {
            // Damit der Text nicht vom Icon überlagert wird
            this.jQueryControl.addClass(this.isInGridClassName);
        } else {
            this.jQueryControl.removeClass(this.isInGridClassName);
        }
        
        // 1 Pixel Toleranz im Firefox. Firefox und Chrome liefern aus aktuell unerklärlichen Gründen abweichende Werte in Höhe von 1 Pixel
        const tolerancePX = Utilities.isFirefox() ? 1 : 0; 
        
        const hasVerticalScrollbar = el.scrollHeight - el.clientHeight > tolerancePX;
        if (hasVerticalScrollbar) {
            this.jQueryControl.addClass(this.verticalScrollableClassName);
        } else {
            this.jQueryControl.removeClass(this.verticalScrollableClassName);
        }
        
        const hasHorizontalScrollbar = el.scrollWidth - el.clientWidth > tolerancePX;
        if (hasHorizontalScrollbar)
            this.jQueryControl.addClass(this.horizontalScrollableClassName);
        else
            this.jQueryControl.removeClass(this.horizontalScrollableClassName);
    }

    protected getScrollbarSpace(): number {
        return 17;
    }
}

ComponentManager.setControl('MultilineTextBox', MultilineTextBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');